import { useState, useEffect, useCallback } from 'react';
import React, { useState as useReactState } from 'react';
import { Link } from 'react-router-dom';
// ─────────────────────────────────────────────────────────────────
// useCookieConsent.ts
// Hook — manages consent state, persists to localStorage,
// fires/kills tracking scripts based on user choices.
// ─────────────────────────────────────────────────────────────────


export interface CookiePreferences {
  necessary: true;         // always true, cannot be toggled
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
}

export type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'customised';

const STORAGE_KEY = 'zimchat_cookie_consent';
const VERSION = '1';  // bump this to re-show banner after policy change

export interface ConsentRecord {
  version: string;
  status: ConsentStatus;
  preferences: CookiePreferences;
  timestamp: number;
}

const DEFAULT_PREFS: CookiePreferences = { necessary: true, analytics: false, functional: false, marketing: false };

function readStored(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: ConsentRecord = JSON.parse(raw);
    if (parsed.version !== VERSION) return null; // re-prompt on version change
    return parsed;
  } catch { return null; }
}

function writeStored(record: ConsentRecord) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch {}
}

// ── Analytics / tracking loaders ──────────────────────────────
const GA_ID = process.env.REACT_APP_GA_ID || '';  // e.g. G-XXXXXXXXXX
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || '';

function loadGoogleAnalytics() {
  if (!GA_ID || document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) { (window as any).dataLayer.push(args); }
  (window as any).gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID, { anonymize_ip: true });
}

function killGoogleAnalytics() {
  // Set opt-out flag — GA respects this
  if (GA_ID) (window as any)[`ga-disable-${GA_ID}`] = true;
  // Remove cookie
  ['_ga', '_gid', `_ga_${GA_ID.replace('G-', '')}`].forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
  });
}

function loadFacebookPixel() {
  if (!FB_PIXEL_ID || (window as any).fbq) return;
  const n: any = function (...args: any[]) { n.callMethod ? n.callMethod(...args) : n.queue.push(args); };
  (window as any).fbq = n;
  n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  (window as any).fbq('init', FB_PIXEL_ID);
  (window as any).fbq('track', 'PageView');
}

function killFacebookPixel() {
  document.cookie = `_fbp=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
}

function applyPreferences(prefs: CookiePreferences) {
  if (prefs.analytics) { loadGoogleAnalytics(); }
  else { killGoogleAnalytics(); }

  if (prefs.marketing) { loadFacebookPixel(); }
  else { killFacebookPixel(); }
}

// ── Hook ──────────────────────────────────────────────────────
export function useCookieConsent() {
  const stored = readStored();
  const [status, setStatus] = useState<ConsentStatus>(stored?.status ?? 'pending');
  const [preferences, setPreferences] = useState<CookiePreferences>(stored?.preferences ?? DEFAULT_PREFS);
  const [showBanner, setShowBanner] = useState(!stored);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    if (stored) applyPreferences(stored.preferences);
  }, []); // eslint-disable-line

  const save = useCallback((newStatus: ConsentStatus, newPrefs: CookiePreferences) => {
    const record: ConsentRecord = { version: VERSION, status: newStatus, preferences: newPrefs, timestamp: Date.now() };
    writeStored(record);
    setStatus(newStatus);
    setPreferences(newPrefs);
    setShowBanner(false);
    setShowSettings(false);
    applyPreferences(newPrefs);
  }, []);

  const acceptAll = useCallback(() => {
    save('accepted', { necessary: true, analytics: true, functional: true, marketing: true });
  }, [save]);

  const rejectAll = useCallback(() => {
    save('rejected', { necessary: true, analytics: false, functional: false, marketing: false });
  }, [save]);

  const saveCustom = useCallback((prefs: CookiePreferences) => {
    save('customised', prefs);
  }, [save]);

  const resetConsent = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
    setStatus('pending');
    setPreferences(DEFAULT_PREFS);
    setShowBanner(true);
  }, []);

  return {
    status, preferences, showBanner, showSettings,
    setShowSettings,
    acceptAll, rejectAll, saveCustom, resetConsent,
    hasConsented: status !== 'pending',
  };
}

// ─────────────────────────────────────────────────────────────────
// CookieConsent.tsx
// The banner + settings drawer.
// Import this once in your App.tsx:
//   const consent = useCookieConsent();
//   return <><CookieConsent consent={consent} /><YourRoutes /></>
// ─────────────────────────────────────────────────────────────────


interface CookieConsentProps {
  consent: ReturnType<typeof useCookieConsent>;
}

export function CookieConsent({ consent }: CookieConsentProps) {
  const { showBanner, showSettings, setShowSettings, acceptAll, rejectAll, saveCustom, preferences } = consent;

  const [draft, setDraft] = useReactState({
    analytics: preferences.analytics,
    functional: preferences.functional,
    marketing: preferences.marketing,
  });

  // Sync draft when settings panel opens
  const openSettings = () => {
    setDraft({ analytics: preferences.analytics, functional: preferences.functional, marketing: preferences.marketing });
    setShowSettings(true);
  };

  if (!showBanner && !showSettings) return null;

  return (
    <>
      <style>{`
        @keyframes slideUpBanner { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fadeInOverlay { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInPanel { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .cookie-toggle { position: relative; width: 42px; height: 24px; display: inline-block; }
        .cookie-toggle input { opacity: 0; width: 0; height: 0; }
        .cookie-toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background: #1e2330; border-radius: 24px; transition: 0.2s; }
        .cookie-toggle input:checked + .cookie-toggle-slider { background: #5B4FE8; }
        .cookie-toggle-slider:before { position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px; background: white; border-radius: 50%; transition: 0.2s; }
        .cookie-toggle input:checked + .cookie-toggle-slider:before { transform: translateX(18px); }
        .cookie-toggle input:disabled + .cookie-toggle-slider { opacity: 0.5; cursor: not-allowed; }
        .cookie-btn-primary { padding: 10px 22px; background: #5B4FE8; color: white; border: none; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit; transition: opacity 0.15s; white-space: nowrap; }
        .cookie-btn-primary:hover { opacity: 0.85; }
        .cookie-btn-ghost { padding: 10px 18px; background: transparent; color: #94a3b8; border: 1px solid #1e2330; border-radius: 8px; font-weight: 500; font-size: 13px; cursor: pointer; font-family: inherit; transition: all 0.15s; white-space: nowrap; }
        .cookie-btn-ghost:hover { border-color: #334155; color: #e2e8f0; }
        .cookie-btn-text { background: none; border: none; color: #64748b; font-size: 12px; cursor: pointer; font-family: inherit; text-decoration: underline; padding: 0; }
        .cookie-btn-text:hover { color: #94a3b8; }
        @media (max-width: 600px) { .cookie-banner-actions { flex-direction: column !important; } .cookie-banner-actions button { width: 100% !important; } }
      `}</style>

      {/* ── BANNER ── */}
      {showBanner && !showSettings && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, animation: 'slideUpBanner 0.35s ease' }}>
          {/* Backdrop blur strip */}
          <div style={{ background: 'rgba(15,17,23,0.97)', backdropFilter: 'blur(16px)', borderTop: '1px solid #1e2330', padding: '20px 24px' }}>
            <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>

              {/* Icon + text */}
              <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flex: 1, minWidth: '280px' }}>
                <span style={{ fontSize: '24px', flexShrink: 0, marginTop: '2px' }}>🍪</span>
                <div>
                  <div style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '14px', marginBottom: '4px', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    We use cookies
                  </div>
                  <div style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.6', fontFamily: "'DM Sans', system-ui, sans-serif" }}>
                    We use essential cookies to make this site work, and optional analytics cookies to understand how it's used. You choose what else is on.{' '}
                    <Link to="/cookies" style={{ color: '#818cf8', textDecoration: 'none' }}>Cookie Policy</Link>
                    {' · '}
                    <Link to="/privacy" style={{ color: '#818cf8', textDecoration: 'none' }}>Privacy Policy</Link>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="cookie-banner-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                <button className="cookie-btn-text" onClick={openSettings}>Manage preferences</button>
                <button className="cookie-btn-ghost" onClick={rejectAll}>Reject optional</button>
                <button className="cookie-btn-primary" onClick={acceptAll}>Accept all</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SETTINGS PANEL ── */}
      {showSettings && (
        <>
          {/* Overlay */}
          <div onClick={() => setShowSettings(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9998, animation: 'fadeInOverlay 0.2s ease' }} />

          {/* Drawer */}
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', maxWidth: '100vw', background: '#15181f', borderLeft: '1px solid #1e2330', zIndex: 9999, display: 'flex', flexDirection: 'column', animation: 'slideInPanel 0.3s ease', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid #1e2330', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ color: '#e2e8f0', fontWeight: '700', fontSize: '17px', marginBottom: '2px' }}>Cookie Settings</div>
                <div style={{ color: '#475569', fontSize: '12px' }}>Manage your tracking preferences</div>
              </div>
              <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '20px', padding: '4px', lineHeight: 1 }}>✕</button>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

              {/* Necessary */}
              <CookieCategory
                icon="🔒"
                name="Strictly Necessary"
                description="Essential for the website to work — login sessions, security tokens, and your saved preferences. These cannot be turned off."
                locked
                enabled
                examples={['zimchat_session — keeps you logged in', 'zimchat_csrf — security protection', 'zimchat_prefs — your display preferences']}
              />

              {/* Analytics */}
              <CookieCategory
                icon="📊"
                name="Analytics"
                description="Helps us understand how people use the Platform so we can improve it. All data is anonymised — we cannot identify you individually."
                enabled={draft.analytics}
                onToggle={v => setDraft(d => ({ ...d, analytics: v }))}
                examples={['Google Analytics — pages visited, time on site', '_ga cookies — 2 year expiry']}
              />

              {/* Functional */}
              <CookieCategory
                icon="⚙️"
                name="Functional"
                description="Enables enhanced features like remembering your onboarding progress and hiding tutorial prompts you've already seen."
                enabled={draft.functional}
                onToggle={v => setDraft(d => ({ ...d, functional: v }))}
                examples={['zimchat_onboarding — 30 days', 'zimchat_tutorial — 1 year']}
              />

              {/* Marketing */}
              <CookieCategory
                icon="📣"
                name="Marketing"
                description="Used on our public marketing pages to measure how effective our ads are. We never show ads inside the dashboard."
                enabled={draft.marketing}
                onToggle={v => setDraft(d => ({ ...d, marketing: v }))}
                examples={['Facebook Pixel — ad conversion tracking', 'Google Ads conversion — 90 days']}
              />

              <div style={{ marginTop: '16px', padding: '14px', background: '#0f1117', border: '1px solid #1e2330', borderRadius: '10px' }}>
                <div style={{ color: '#475569', fontSize: '12px', lineHeight: '1.6' }}>
                  We never sell your data. Analytics data is anonymised. For full details, see our{' '}
                  <Link to="/cookies" style={{ color: '#818cf8', textDecoration: 'none' }} onClick={() => setShowSettings(false)}>Cookie Policy</Link>
                  {' '}and{' '}
                  <Link to="/privacy" style={{ color: '#818cf8', textDecoration: 'none' }} onClick={() => setShowSettings(false)}>Privacy Policy</Link>.
                </div>
              </div>
            </div>

            {/* Footer actions */}
            <div style={{ padding: '20px 24px', borderTop: '1px solid #1e2330', display: 'flex', gap: '10px' }}>
              <button className="cookie-btn-ghost" style={{ flex: 1 }} onClick={rejectAll}>Reject optional</button>
              <button
                className="cookie-btn-primary"
                style={{ flex: 2 }}
                onClick={() => saveCustom({ necessary: true, analytics: draft.analytics, functional: draft.functional, marketing: draft.marketing })}
              >
                Save preferences
              </button>
            </div>

            {/* Accept all link */}
            <div style={{ textAlign: 'center', paddingBottom: '16px' }}>
              <button className="cookie-btn-text" onClick={acceptAll}>Accept all cookies</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ── Cookie category row ────────────────────────────────────────
function CookieCategory({ icon, name, description, enabled, locked, onToggle, examples }: {
  icon: string; name: string; description: string; enabled: boolean;
  locked?: boolean; onToggle?: (v: boolean) => void; examples: string[];
}) {
  const [open, setOpen] = useReactState(false);
  return (
    <div style={{ marginBottom: '16px', background: '#0f1117', border: '1px solid #1e2330', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '20px', flexShrink: 0 }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#e2e8f0', fontWeight: '600', fontSize: '14px', marginBottom: '3px' }}>{name}</div>
          <div style={{ color: '#64748b', fontSize: '12px', lineHeight: '1.5' }}>{description}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <button onClick={() => setOpen(o => !o)} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '11px', padding: '4px 8px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
            {open ? '▲' : '▼'}
          </button>
          <label className="cookie-toggle">
            <input type="checkbox" checked={enabled} disabled={locked} onChange={e => onToggle && onToggle(e.target.checked)} />
            <span className="cookie-toggle-slider" />
          </label>
        </div>
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid #1e2330' }}>
          <div style={{ paddingTop: '12px', color: '#64748b', fontSize: '11px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>Cookies in this category</div>
          {examples.map((ex, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '5px' }}>
              <span style={{ color: '#334155', marginTop: '2px', fontSize: '10px' }}>●</span>
              <span style={{ color: '#475569', fontSize: '12px' }}>{ex}</span>
            </div>
          ))}
          {locked && (
            <div style={{ marginTop: '10px', padding: '8px 10px', background: '#1e2330', borderRadius: '8px', color: '#475569', fontSize: '11px' }}>
              🔒 These cannot be disabled as the site will not function without them.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Floating "cookie settings" button for footer/settings page ──
export function CookieSettingsButton({ consent }: { consent: ReturnType<typeof useCookieConsent> }) {
  return (
    <button
      onClick={() => consent.setShowSettings(true)}
      style={{ background: 'none', border: '1px solid #1e2330', color: '#64748b', padding: '7px 16px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif", display: 'inline-flex', alignItems: 'center', gap: '6px', transition: 'all 0.15s' }}
    >
      🍪 Cookie Settings
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Usage in App.tsx:
//
//   import { useCookieConsent, CookieConsent } from './components/CookieConsent';
//
//   export default function App() {
//     const consent = useCookieConsent();
//     return (
//       <>
//         <CookieConsent consent={consent} />
//         <Router>
//           ...your routes...
//         </Router>
//       </>
//     );
//   }
//
// To open settings from your footer:
//   import { CookieSettingsButton } from './components/CookieConsent';
//   <CookieSettingsButton consent={consent} />
//
// Environment variables needed in .env:
//   REACT_APP_GA_ID=G-XXXXXXXXXX
//   REACT_APP_FB_PIXEL_ID=123456789
// ─────────────────────────────────────────────────────────────────
